import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { AwsClient } from "aws4fetch";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif", "video/mp4", "video/webm"]);

function s3Config() {
  const accessKeyId = process.env.AWS_ACCESS_KEY_ID;
  const secretAccessKey = process.env.AWS_SECRET_ACCESS_KEY;
  const bucket = process.env.AWS_S3_BUCKET;
  const region = process.env.AWS_REGION || "us-east-1";
  if (!accessKeyId || !secretAccessKey || !bucket) {
    throw new Error("AWS S3 is not configured. Set AWS_ACCESS_KEY_ID, AWS_SECRET_ACCESS_KEY, AWS_S3_BUCKET.");
  }
  return { accessKeyId, secretAccessKey, bucket, region };
}

function publicUrlFor(bucket: string, region: string, key: string) {
  const host = region === "us-east-1" ? `${bucket}.s3.amazonaws.com` : `${bucket}.s3.${region}.amazonaws.com`;
  return `https://${host}/${key.split("/").map(encodeURIComponent).join("/")}`;
}

/**
 * Returns a presigned PUT URL so the browser can upload directly to S3.
 * Caller (signed-in user) gets a key namespaced under their user id.
 */
export const getS3UploadUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({
      contentType: z.string().min(1).max(80),
      prefix: z.string().regex(/^[a-z0-9_-]+$/i).max(40).default("uploads"),
      ext: z.string().regex(/^[a-z0-9]{1,8}$/i),
    }).parse(input),
  )
  .handler(async ({ data, context }) => {
    if (!ALLOWED_TYPES.has(data.contentType)) {
      throw new Error(`Unsupported content type: ${data.contentType}`);
    }
    const { accessKeyId, secretAccessKey, bucket, region } = s3Config();
    const aws = new AwsClient({ accessKeyId, secretAccessKey, service: "s3", region });

    const key = `${data.prefix}/${context.userId}/${crypto.randomUUID()}.${data.ext.toLowerCase()}`;
    const host = region === "us-east-1" ? `${bucket}.s3.amazonaws.com` : `${bucket}.s3.${region}.amazonaws.com`;
    const unsigned = new Request(`https://${host}/${key}?X-Amz-Expires=900`, {
      method: "PUT",
      headers: { "Content-Type": data.contentType },
    });
    const signed = await aws.sign(unsigned, { aws: { signQuery: true } });

    return {
      uploadUrl: signed.url,
      publicUrl: publicUrlFor(bucket, region, key),
      key,
      contentType: data.contentType,
    };
  });
